import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { useCallback, useState, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'

import { useSnackbar } from 'notistack'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Form, FormikProvider, useFormik } from 'formik'
// material
import { LoadingButton } from '@material-ui/lab'
import {
  Box,
  Card,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
  FormHelperText,
  FormControlLabel,
  Autocomplete,
  IconButton,
} from '@material-ui/core'
// utils
import { fData } from '../../../utils/formatNumber'
import fakeRequest from '../../../utils/fakeRequest'
// routes
import { PATH_DASHBOARD } from '../../../routes/paths'
//
import Label from '../../Label'
import { UploadAvatar } from '../../upload'
import { MotionInView, varFadeInUp } from 'src/components/animate'
import { styled } from '@material-ui/styles'
import { ArrowDownward } from '@material-ui/icons'
import { REST_API } from 'src/DefaultValues/DefaultValues'
import axios from 'axios'
import { useEffect } from 'react'

const Region = [
  { id: 1, label: 'North America' },
  { id: 2, label: 'South America' },
  { id: 3, label: 'Europe' },
  { id: 4, label: 'Middle East' },
  { id: 5, label: 'APAC' },
  { id: 6, label: 'India' },
]

const Inquiry_Type = [
  { id: 1, label: 'Alumini' },
  { id: 2, label: 'Career Seekers' },
  { id: 3, label: 'Investor Relations' },
  { id: 4, label: 'Media' },
  { id: 5, label: 'Partners' },
  { id: 6, label: 'Request For Services' },
  { id: 7, label: 'Synapse' },
  { id: 8, label: 'STOP' },
]

const CustomTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#97999b',
    },
    '&:hover fieldset': {
      borderColor: '#000048',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#6aa2dc',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#000048',
    fontSize: '17px',
    marginTop: '-3px',
    fontWeight: '400',
    fontFamily:
      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
  },
})

export default function HomeContactForm({ noTitle, setNoTitle }) {
  const recaptchaRef = useRef(null)

  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const params = useParams()
  const { pathname } = useLocation()
  console.log(pathname.split('/')[1])
  const pathStop = pathname.split('/')[1]
  const [stop, setStop] = useState(false)

  useEffect(() => {
    if (pathStop === 'stop') {
      setStop(true)
    }
  }, [pathStop])

  const NewUserSchema = Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .matches(
        /^[a-zA-Z\s]+$/,
        'Name should not contain numbers or special characters',
      )
      .min(2, 'Too Short!')
      .max(50, 'Too Long!'),
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address')
      .test(
        'at-least-one-letter',
        'Email must contain at least one letter before @',
        (value) => {
          if (!value) return false
          const beforeAtSymbol = value.split('@')[0]
          return /[a-zA-Z]/.test(beforeAtSymbol)
        },
      ),
    contactNumber: Yup.string()
      .required('Contact Number is required')
      .min(10, '10 Numbers Should Type')
      .max(10, 'More Than 10 Numbers Is Not Allowed'),
    organization: Yup.string().required('Organization is required'),
    message: Yup.string().required('Message Number is required'),
    region: Yup.object().nullable().required('Region is required'),
    inquiry_type: Yup.object().nullable().required('Inquiry Type is required'),
    captcha: Yup.string().required('Please verify that you are not a robot'),
  })


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      email: '',
      contactNumber: '',
      organization: '',
      message: '',
      region: null,
      inquiry_type: stop ? { id: 8, label: 'STOP' } : null,
      captcha: '',
    },

    validationSchema: NewUserSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setErrors }) => {
      try {
        const response = await axios.post(
          `${REST_API}/webservice/contact-form`,
          { values },
        )
        console.log('Form Values :', response.data)
        await fakeRequest(500)
        resetForm()
        if (recaptchaRef.current) {
          recaptchaRef.current.reset()
        }
        setSubmitting(false)
        enqueueSnackbar('Data Submitted Successfully', { variant: 'success' })
      } catch (error) {
        console.error(error)
        setSubmitting(false)
        setErrors(error)
      }
    },

  })

  const {
    errors,
    values,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    getFieldProps,
  } = formik

  return (
    <FormikProvider value={formik}>
      <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
        {!noTitle ? (
          <MotionInView sx={{ mt: 4 }} variants={varFadeInUp}>
            <Typography
              sx={{
                mb: 2,
                fontSize: '34px',
                fontWeight: '600',
                color: '#019bdc',
                fontFamily:
                  '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
              }}
            >
              Get answers to your questions
            </Typography>
            <Typography
              sx={{
                mb: 4,
                fontSize: '19px',
                fontWeight: '400',
                color: '#000049',
                fontFamily:
                  '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
              }}
            >
              Our clients turn to us to help them reimagine ways of working with
              technology.
            </Typography>
          </MotionInView>
        ) : (
          ''
        )}
        <Grid container sx={{ mt: noTitle ? 0 : 4 }}>
          <Grid item xs={12} md={12}>
            <Stack spacing={3}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 3, sm: 2 }}
              >
                <TextField
                  fullWidth
                  label="Name*"
                  {...getFieldProps('name')}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#97999b',
                      },
                      '&:hover fieldset': {
                        borderColor: '#000048',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#6aa2dc',
                      },
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      color: '#000048',
                      fontSize: '17px',
                      marginTop: '-3px',
                      fontWeight: '400',
                      fontFamily:
                        '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    },
                  }}
                  inputProps={{
                    'aria-required': 'true',
                    'aria-invalid': Boolean(touched.name && errors.name),
                  }}
                />
                <TextField
                  fullWidth
                  label="Email*"
                  {...getFieldProps('email')}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#97999b',
                      },
                      '&:hover fieldset': {
                        borderColor: '#000048',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#6aa2dc',
                      },
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      color: '#000048',
                      fontSize: '17px',
                      marginTop: '-3px',
                      fontWeight: '400',
                      fontFamily:
                        '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    },
                  }}
                  inputProps={{
                    'aria-required': 'true',
                    'aria-invalid': Boolean(touched.email && errors.email),
                  }}
                />
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 3, sm: 2 }}
              >
                <TextField
                  fullWidth
                  label="Organization*"
                  {...getFieldProps('organization')}
                  error={Boolean(touched.organization && errors.organization)}
                  helperText={touched.organization && errors.organization}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#97999b',
                      },
                      '&:hover fieldset': {
                        borderColor: '#000048',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#6aa2dc',
                      },
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      color: '#000048',
                      fontSize: '17px',
                      marginTop: '-3px',
                      fontWeight: '400',
                      fontFamily:
                        '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    },
                  }}
                  inputProps={{
                    'aria-required': 'true',
                    'aria-invalid': Boolean(
                      touched.organization && errors.organization,
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Contact Number*"
                  {...getFieldProps('contactNumber')}
                  error={Boolean(touched.contactNumber && errors.contactNumber)}
                  helperText={touched.contactNumber && errors.contactNumber}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#97999b',
                      },
                      '&:hover fieldset': {
                        borderColor: '#000048',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#6aa2dc',
                      },
                    },
                  }}
                  InputProps={{
                    type: 'number',
                    inputProps: {
                      min: '0',
                      'aria-required': 'true',
                      'aria-invalid': Boolean(
                        touched.contactNumber && errors.contactNumber,
                      ),
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      color: '#000048',
                      fontSize: '17px',
                      marginTop: '-3px',
                      fontWeight: '400',
                      fontFamily:
                        '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    },
                  }}
                />
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 3, sm: 2 }}
              >
                <Autocomplete
                  fullWidth
                  options={Region}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      label="Region*"
                      error={Boolean(touched.region && errors.region)}
                      helperText={touched.region && errors.region}
                      InputLabelProps={{
                        style: {
                          color: '#000048',
                          fontSize: '17px',
                          fontWeight: '400',
                          fontFamily:
                            '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                        },
                      }}
                      inputProps={{
                        ...params.inputProps,
                        'aria-required': 'true',
                        'aria-invalid': Boolean(
                          touched.region && errors.region,
                        ),
                      }}
                    />
                  )}
                  value={values.region}
                  onChange={(event, newValue) => {
                    setFieldValue('region', newValue)
                  }}
                />

                {stop ? (
                  <TextField
                    fullWidth
                    disabled
                    aria-readonly
                    value={values.inquiry_type?.label || 'STOP'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#97999b',
                          opacity: 0.7,
                        },
                        // '&:hover fieldset': {
                        //   borderColor: '#000048',
                        // },
                        // '&.Mui-focused fieldset': {
                        //   borderColor: '#6aa2dc',
                        // },
                      },
                    }}
                    InputLabelProps={{
                      style: {
                        color: '#000048',
                        fontSize: '17px',
                        marginTop: '-3px',
                        fontWeight: '400',
                        fontFamily:
                          '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                      },
                    }}
                  />
                ) : (
                  <Autocomplete
                    fullWidth
                    options={Inquiry_Type}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Inquiry Type*"
                        error={Boolean(
                          touched.inquiry_type && errors.inquiry_type,
                        )}
                        helperText={touched.inquiry_type && errors.inquiry_type}
                        InputLabelProps={{
                          style: {
                            color: '#000048',
                            fontSize: '17px',
                            fontWeight: '400',
                            fontFamily:
                              '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                          },
                        }}
                        inputProps={{
                          ...params.inputProps,
                          'aria-required': 'true',
                          'aria-invalid': Boolean(
                            touched.inquiry_type && errors.inquiry_type,
                          ),
                        }}
                      />
                    )}
                    value={values.inquiry_type}
                    //             value={stop
                    // ? Inquiry_Type.find((opt) => opt.value === "stop")
                    // : values.inquiry_type}
                    onChange={(event, newValue) => {
                      setFieldValue('inquiry_type', newValue)
                    }}
                  />
                )}
              </Stack>

              <TextField
                fullWidth
                label="Message*"
                multiline
                rows={4}
                {...getFieldProps('message')}
                error={Boolean(touched.message && errors.message)}
                helperText={touched.message && errors.message}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#97999b',
                    },
                    '&:hover fieldset': {
                      borderColor: '#000048',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6aa2dc',
                    },
                  },
                }}
                InputLabelProps={{
                  style: {
                    color: '#000048',
                    fontSize: '17px',
                    marginTop: '-3px',
                    fontWeight: '400',
                    fontFamily:
                      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                  },
                }}
                inputProps={{
                  'aria-required': 'true',
                  'aria-invalid': Boolean(touched.message && errors.message),
                }}
              />

              <Box sx={{ mt: 2 }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6LcPtucsAAAAAFMk3e4xnEUlI_Y27PjOUtVsANtj" // Replace with your real site key
                  onChange={(value) => setFieldValue('captcha', value)}
                />
                {touched.captcha && errors.captcha && (
                  <FormHelperText error sx={{ px: 2, mt: 1 }}>
                    {errors.captcha}
                  </FormHelperText>
                )}
              </Box>

              <Box

                sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}
              >
                <LoadingButton
                  type="submit"
                  size="large"
                  sx={{
                    borderRadius: 3,
                    color: '#fff',
                    fontSize: '20px',
                    fontWeight: '500',
                    fontFamily:
                      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                  }}
                  endIcon={
                    <Stack
                      className="bx--right-arrow"
                      sx={{ fontSize: '1.1rem' }}
                    />
                  }
                  variant="contained"
                  loading={isSubmitting}
                >
                  {'Submit'}
                </LoadingButton>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  )
}
